import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Cart from 'utilities/storefrontCart';
import { SHELL, MAX, CARD, SUB_INPUT, BUTTON, brandPath, formatMoney } from './styles';
import useBrand from './useBrand';
import StorefrontNav from './StorefrontNav';
import { ShellMessage, Row } from './shellHelpers';

/**
 * Cart page — line items + totals.
 *
 * Cart state is per-brand (see `utilities/storefrontCart`) so a shopper
 * with multiple brand storefronts open in tabs doesn't see one brand's
 * items leak into another.
 */
export default function StorefrontCart() {
  const { brand, error } = useBrand();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const accent = brand?.brand_color || '#422AFB';

  useEffect(() => {
    if (!brand?._id) return;
    setItems(Cart.loadCart(brand._id));
  }, [brand?._id]);

  const refresh = () => setItems(Cart.loadCart(brand._id));
  const setQty  = (variant_id, q) => { Cart.setQuantity(brand._id, variant_id, q); refresh(); };
  const remove  = (variant_id)    => { Cart.removeFromCart(brand._id, variant_id); refresh(); };

  const totals = useMemo(() => Cart.cartTotals(items), [items]);

  if (error) return <ShellMessage>{error}</ShellMessage>;
  if (!brand) return <ShellMessage>Loading…</ShellMessage>;

  return (
    <div style={SHELL}>
      <StorefrontNav brand={brand} />
      <div style={MAX}>
        <h1 style={{ fontSize: 28, margin: '8px 0 24px' }}>Cart</h1>
        {items.length === 0 && (
          <div style={{ ...CARD }}>
            <div style={{ color: '#64748b', marginBottom: 14 }}>Your cart is empty.</div>
            <button onClick={() => navigate(brandPath(brand, '/shop'))} style={BUTTON(accent)}>
              Continue shopping
            </button>
          </div>
        )}
        {items.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)', gap: 24 }}>
            <div style={CARD}>
              {items.map((it) => (
                <div key={it.variant_id} style={{
                  display: 'flex', gap: 14, padding: '12px 0',
                  borderBottom: '1px solid #f1f5f9',
                }}>
                  {it.image_url
                    ? <img src={it.image_url} alt='' style={{ width: 72, height: 72, borderRadius: 8, objectFit: 'cover' }} />
                    : <div style={{ width: 72, height: 72, borderRadius: 8, background: '#f1f5f9' }} />}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600 }}>{it.name}</div>
                    {it.variant_name && <div style={{ fontSize: 12, color: '#64748b' }}>{it.variant_name}</div>}
                    <div style={{ fontSize: 13, color: '#0f172a', marginTop: 2 }}>{formatMoney(it.unit_price, it.currency)}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type='number' min='1'
                      value={it.quantity}
                      onChange={(e) => setQty(it.variant_id, Number(e.target.value))}
                      style={{ ...SUB_INPUT, width: 70, padding: '6px 8px' }}
                    />
                    <button
                      onClick={() => remove(it.variant_id)}
                      style={{ background: 'transparent', color: '#ef4444', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div style={CARD}>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#475569', marginBottom: 10 }}>Summary</div>
              <Row label='Items'>{totals.count}</Row>
              <Row label='Subtotal'>{formatMoney(totals.subtotal, totals.currency)}</Row>
              <Row label='Taxes' muted>calculated at checkout</Row>
              <Row label='Shipping' muted>calculated at checkout</Row>
              <button
                onClick={() => navigate(brandPath(brand, '/checkout'))}
                style={{ ...BUTTON(accent), width: '100%', marginTop: 14 }}
              >
                Checkout
              </button>
              <button
                onClick={() => navigate(brandPath(brand, '/shop'))}
                style={{ ...BUTTON(accent, true), width: '100%', marginTop: 8 }}
              >
                Continue shopping
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
